import type { LinkedTenant } from '@shared/types/auth';
import { openLink } from '@shared/lib/openLink';
import { getNotificationTarget } from './notificationTargets';
import type { Notification } from './api';
import type { PendingTenantSwitch } from './tenantSwitchPromptStore';

export type RoutableNotification = Pick<
  Notification,
  'notification_id' | 'action_url' | 'notification_type' | 'related_id' | 'related_entity_type' | 'module' | 'metadata' | 'tenant_id'
>;

export interface RouteNotificationDeps {
  activeTenantId: string | null;
  linkedTenants: LinkedTenant[];
  /** Navigate within the current tenant (SPA navigate or global navigateTo). */
  navigate: (path: string) => void;
  /** Open the confirm-and-switch prompt for a notification belonging to another linked tenant. */
  requestSwitch: (req: PendingTenantSwitch) => void;
}

/**
 * Resolve a notification (push or in-app) to its destination and act on it:
 *  - external link → open it
 *  - same tenant   → navigate
 *  - other linked tenant → prompt to switch, then route after switching
 *  - other unlinked tenant → best-effort navigate in the current tenant
 * A notification with no page of its own opens in the bell on home, so the tap still shows it.
 */
export function routeNotification(n: RoutableNotification, deps: RouteNotificationDeps): void {
  const target =
    getNotificationTarget(n) ??
    (n.notification_id ? { kind: 'internal' as const, path: `/app/home?notification=${n.notification_id}` } : null);
  if (!target) return;

  if (target.kind === 'external') {
    void openLink(target.url);
    return;
  }

  const targetTenantId = n.tenant_id?.trim();
  if (targetTenantId && deps.activeTenantId && targetTenantId !== deps.activeTenantId) {
    const linked = deps.linkedTenants.find((t) => t.tenant_id === targetTenantId);
    if (linked) {
      deps.requestSwitch({ tenantId: targetTenantId, tenantName: linked.name, path: target.path });
      return;
    }
    // Not a tenant this user can access — fall through to a best-effort navigate.
  }

  deps.navigate(target.path);
}
